import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RecurringConfig,
    DEFAULT_RECURRING_CONFIG,
    RECURRING_LABELS,
    CalendarType,
    RecurrenceFrequency,
    getDefaultRecurringByEventType,
} from '../../types/recurringTypes';

interface RecurringToggleProps {
    config: RecurringConfig | undefined;
    eventType: string;
    onChange: (config: RecurringConfig) => void;
    className?: string;
}

const RecurringToggle: React.FC<RecurringToggleProps> = ({
    config,
    eventType,
    onChange,
    className = '',
}) => {
    const currentConfig: RecurringConfig = config || {
        ...DEFAULT_RECURRING_CONFIG,
        ...getDefaultRecurringByEventType(eventType),
    };

    const handleToggle = () => {
        const newConfig: RecurringConfig = {
            ...currentConfig,
            ...getDefaultRecurringByEventType(eventType),
            enabled: !currentConfig.enabled,
        };
        onChange(newConfig);
    };

    const handleCalendarTypeChange = (calendarType: CalendarType) => {
        onChange({ ...currentConfig, calendarType });
    };

    const handleFrequencyChange = (frequency: RecurrenceFrequency) => {
        onChange({ ...currentConfig, frequency });
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Toggle Button */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white/70">
                    Lặp lại sự kiện
                </label>
                <motion.button
                    type="button"
                    onClick={handleToggle}
                    whileTap={{ scale: 0.95 }}
                    className={`
                        relative w-12 h-6 rounded-full transition-colors
                        ${currentConfig.enabled ? 'bg-green-500' : 'bg-white/20'}
                    `}
                >
                    <motion.div
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow"
                        animate={{ left: currentConfig.enabled ? '1.5rem' : '0.25rem' }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                </motion.button>
            </div>

            {/* Expanded Options */}
            <AnimatePresence>
                {currentConfig.enabled && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-3 pt-2 border-t border-white/10">
                            {/* Calendar Type */}
                            <div>
                                <label className="block text-xs text-white/50 mb-2">
                                    Lịch sử dụng
                                </label>
                                <div className="flex gap-2">
                                    {(['lunar', 'solar'] as CalendarType[]).map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => handleCalendarTypeChange(type)}
                                            className={`
                                                flex-1 py-2 px-3 rounded-lg text-sm font-medium
                                                transition-all
                                                ${currentConfig.calendarType === type
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                                                }
                                            `}
                                        >
                                            {RECURRING_LABELS.calendarType[type]}
                                        </button>
                                    ))}
                                </div>
                                {eventType === 'gio' && currentConfig.calendarType === 'lunar' && (
                                    <p className="text-xs text-green-400 mt-1">
                                        ✓ Khuyến nghị cho ngày Giỗ
                                    </p>
                                )}
                            </div>

                            {/* Frequency */}
                            <div>
                                <label className="block text-xs text-white/50 mb-2">
                                    Tần suất
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {(['yearly', 'monthly'] as RecurrenceFrequency[]).map((freq) => (
                                        <button
                                            key={freq}
                                            type="button"
                                            onClick={() => handleFrequencyChange(freq)}
                                            className={`
                                                py-2 px-3 rounded-lg text-sm font-medium
                                                transition-all
                                                ${currentConfig.frequency === freq
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                                                }
                                            `}
                                        >
                                            {RECURRING_LABELS.frequency[freq]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RecurringToggle;
